/**
 * @format
 * @module ui/audio-service
 * @description Audio player API endpoints and settings
 */
import tgamConfig from "config/tgam";
import { API_DS_AUDIO, API_DS_AUDIO_LIST, API_PROFILE } from "lib/gamservices";
import { local } from "lib/store";
import { handleJSON, validStatus } from "utils/fetch";
import { getProp } from "utils/utils";

const audioPlayerPreviouslyClosedKey = "tgam.audio-player-closed";

/**
 * Record when the user closes the audio player so that we can decide whether to reveal it on subsequent page loads
 * @param {Boolean} value - whether the user has closed the audio player
 */
export function setAudioPlayerClosedLocalStorage(value) {
  local.set(audioPlayerPreviouslyClosedKey, value);
}

/**
 * Fetch the current local storage value representing whether the audio player was previously closed
 * @returns {Boolean}
 */
export function getAudioPlayerClosedLocalStorage() {
  return local.get(audioPlayerPreviouslyClosedKey);
}

/**
 * Fetch single article audio file from API
 * @param {String} [articleId=""] - the id of the article whose audio is being fetched
 * @returns {Promise<Object>}
 */
export function fetchArticleAudio(articleId = "") {
  const url = `${API_DS_AUDIO}?check=t&id=${articleId}`;
  return fetch(url, {
    method: "GET",
    mode: "cors"
  })
    .then(validStatus)
    .then(handleJSON);
}

/**
 * Fetch multiple articles audio files from API
 * @param {Array<String>} [articleIds=[]] - the list of articles whose audio is being fetched
 * @returns {Promise<Object>}
 */
export function fetchArticleAudioList(articleIds = []) {
  const url = `${API_DS_AUDIO_LIST}?id=${articleIds.join(",")}&check=true`;
  return fetch(url, {
    method: "GET",
    mode: "cors"
  })
    .then(validStatus)
    .then(handleJSON);
}

/**
 * Sets users's data to user management API.
 * @param {Object} data - to set to user management API
 * @returns {Promise}
 */
function setUserAttribute(data) {
  const config = {
    method: "PUT",
    body: JSON.stringify(data),
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    }
  };

  return fetch(`${API_PROFILE}/attribute`, config)
    .then(validStatus)
    .then(handleJSON)
    .catch(console.error);
}

/**
 * Saves user's new speed setting to user management API.
 * @param {Number} newSpeed - the user's new speed setting
 */
export function setUserSpeedSetting(newSpeed) {
  setUserAttribute({ AUDIO_SPEED: newSpeed });
}

/**
 * Saves user's new language setting to user management API.
 * @param {String} newLang - the user's new language setting
 */
export function setUserLangSetting(newLang) {
  setUserAttribute({ AUDIO_LANGUAGE: newLang });
}

/**
 * Returns user's language settings
 * @returns {String} of language code
 */
export function getUserLangSetting() {
  const language = getProp(
    tgamConfig,
    "datalayer.identity.userAttributes.AUDIO_LANGUAGE",
    "url_en_M"
  );
  return language;
}

/**
 * Returns user's speed settings
 * @returns {Number} of speed
 */
export function getUserSpeedSetting() {
  const speed = getProp(tgamConfig, "datalayer.identity.userAttributes.AUDIO_SPEED", 1);
  return Number(speed);
}
